import { defineStore } from 'pinia'
import { useAppStore } from './app'
import { formatarNano } from '@/services/utilidades'
import wsClient from '@/services/websocket'

const STORAGE_KEY = 'quarnx_wallet'
const FAUCET_COOLDOWN_MS = 10_000 // 10s cooldown between faucet requests

export const useWalletStore = defineStore('wallet', {
  state: () => ({
    address: null,
    publicKeyHex: null,
    privateKeyData: null,
    signingKey: null,
    signatureScheme: 'dilithium3',
    balance: 0,
    isConnected: false,
    isLoading: false,
    transactions: [],
    lastFaucetTime: 0,
  }),

  getters: {
    hasWallet: (state) => !!state.address,
    balanceFormatted: (state) => formatarNano(state.balance),
    shortAddress: (state) => {
      if (!state.address) return ''
      return `${state.address.slice(0, 8)}...${state.address.slice(-8)}`
    },
    faucetOnCooldown: (state) => {
      return Date.now() - state.lastFaucetTime < FAUCET_COOLDOWN_MS
    },
  },

  actions: {
    async createWallet(scheme = 'ed25519', password = null) {
      if (this.isLoading) return
      const appStore = useAppStore()
      this.isLoading = true
      this._walletPassword = password
      try {
        let keypair

        if (scheme === 'dilithium3') {
          try {
            const { generateDilithiumKeypair } = await import('@/services/walletCrypto')
            keypair = await generateDilithiumKeypair()
          } catch (e) {
            console.error('[Wallet] Dilithium3 failed:', e.message)
            // Ask user explicitly before falling back
            appStore.showNotification({
              color: 'error',
              message: 'Dilithium3 indisponível. Carteira será criada com Ed25519 (não pós-quântico).',
              timeout: 8000,
            })
            scheme = 'ed25519'
            const { generateKeypair } = await import('@/services/walletCrypto')
            keypair = await generateKeypair()
          }
        } else {
          const { generateKeypair } = await import('@/services/walletCrypto')
          keypair = await generateKeypair()
        }

        this.address = keypair.address
        this.publicKeyHex = keypair.publicKeyHex
        this.privateKeyData = keypair.privateKey
        this.signingKey = keypair._signingKey
        this.signatureScheme = scheme
        this.isConnected = true

        try {
          const { registerWallet } = await import('@/services/walletCrypto')
          const signingKey = scheme === 'dilithium3' ? keypair.privateKey : keypair._signingKey
          await registerWallet(keypair.address, keypair.publicKeyHex, signingKey, scheme)
        } catch (e) {
          console.debug('[Wallet] Registration failed:', e.message)
        }

        // Set global for components that need wallet address
        window.__QUARNX_WALLET_ADDR__ = this.address

        await this._persistToStorage()
        await this.fetchBalance()
        this.initWebSocketListener()

        appStore.showNotification({
          color: 'success',
          message: `Carteira criada: ${this.shortAddress}`,
          timeout: 5000,
        })
      } catch (error) {
        console.debug('[Wallet] Create failed:', error.message)
        appStore.showNotification({
          color: 'error',
          message: 'Erro ao criar carteira',
          timeout: 5000,
        })
      } finally {
        this.isLoading = false
      }
    },

    async restoreFromStorage() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return false

        let data
        try {
          data = JSON.parse(raw)
        } catch {
          this._clearStorage()
          return false
        }

        if (!data || !data.address || !data.publicKeyHex) {
          this._clearStorage()
          return false
        }

        this.address = data.address
        this.publicKeyHex = data.publicKeyHex
        this.signatureScheme = data.signatureScheme || 'ed25519'
        this.isConnected = true
        window.__QUARNX_WALLET_ADDR__ = this.address

        // Restore private key — try encrypted format first, then legacy plain base64
        let privateKeyBase64 = null

        if (data.privateKeyEncrypted) {
          try {
            const { decryptWithPassword } = await import('@/services/walletCrypto')
            // Try user password first (if wallet was created with password protection)
            if (data.hasUserPassword) {
              // Wallet requires user password — will be decrypted on demand
              this._needsPasswordUnlock = true
              this._encryptedKeyData = data.privateKeyEncrypted
              privateKeyBase64 = null
            } else {
              // Legacy: derived password from address
              const password = 'qx-' + data.address.slice(0, 16)
              privateKeyBase64 = await decryptWithPassword(password, data.privateKeyEncrypted)
            }
          } catch {
            privateKeyBase64 = null
          }
        } else if (data.privateKeyBase64) {
          // Backward compatibility: migrate old plain format to encrypted
          privateKeyBase64 = data.privateKeyBase64
        }

        if (privateKeyBase64) {
          try {
            const bytes = Uint8Array.from(atob(privateKeyBase64), c => c.charCodeAt(0))
            this.privateKeyData = bytes

            if (this.signatureScheme === 'dilithium3') {
              // Dilithium3 uses WASM directly, no CryptoKey import needed
              this.signingKey = null
            } else {
              const imported = await crypto.subtle.importKey(
                'pkcs8', bytes, { name: 'Ed25519' }, false, ['sign']
              )
              this.signingKey = imported
            }
          } catch {
            this.signingKey = null
          }

          // Migrate legacy unencrypted key to encrypted format
          if (data.privateKeyBase64 && !data.privateKeyEncrypted) {
            await this._persistToStorage()
          }
        }

        await this.fetchBalance()
        this.initWebSocketListener()
        return true
      } catch {
        this._clearStorage()
        return false
      }
    },

    async _persistToStorage() {
      try {
        const data = {
          address: this.address,
          publicKeyHex: this.publicKeyHex,
          signatureScheme: this.signatureScheme,
        }
        // Encrypt private key before storing (AES-256-GCM + PBKDF2)
        if (this.privateKeyData) {
          const { encryptWithPassword } = await import('@/services/walletCrypto')
          const password = this._walletPassword || 'qx-' + (this.address || '').slice(0, 16)
          const plainBase64 = btoa(String.fromCharCode(...this.privateKeyData))
          data.privateKeyEncrypted = await encryptWithPassword(password, plainBase64)
          data.hasUserPassword = !!this._walletPassword
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
      } catch (e) {
        console.debug('[Wallet] Persist failed:', e.message)
      }
    },

    _clearStorage() {
      try { localStorage.removeItem(STORAGE_KEY) } catch {}
    },

    disconnect() {
      this.address = null
      this.publicKeyHex = null
      this.privateKeyData = null
      this.signingKey = null
      this.signatureScheme = 'ed25519'
      this.balance = 0
      this.isConnected = false
      this.transactions = []
      this._clearStorage()
      window.__QUARNX_WALLET_ADDR__ = null

      const appStore = useAppStore()
      appStore.showNotification({
        color: 'info',
        message: 'Carteira desconectada',
        timeout: 3000,
      })
    },

    async fetchBalance() {
      if (!this.address) return
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.contas.obterPorEndereco(this.address)
        this.balance = data.balance || 0
      } catch {
        // Keep last known balance
      }
    },

    async fetchTransactions() {
      if (!this.address) return
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.contas.obterTransacoes(this.address)
        this.transactions = (data.transactions || []).map(tx => ({
          hash: tx.hash || '',
          from: tx.from || '',
          to: tx.to || '',
          value: tx.value || 0,
          fee: tx.fee || 0,
          status: tx.status || 'confirmed',
          timestamp: tx.timestamp ? new Date(tx.timestamp < 10000000000 ? tx.timestamp * 1000 : tx.timestamp) : new Date(),
        }))
      } catch {
        // Keep existing transactions
      }
    },

    async requestFaucet() {
      if (!this.address) return
      if (this.faucetOnCooldown) {
        const appStore = useAppStore()
        appStore.showNotification({
          color: 'warning',
          message: 'Aguarde antes de solicitar novamente',
          timeout: 3000,
        })
        return
      }

      const appStore = useAppStore()
      this.isLoading = true
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const result = await ExplorerAPI.faucet.solicitar(this.address)
        this.lastFaucetTime = Date.now()
        await this.fetchBalance()
        appStore.showNotification({
          color: 'success',
          message: `Faucet: +${formatarNano(result.amount || 10000000000000)}`,
          timeout: 5000,
        })
      } catch (error) {
        const msg = error?.response?.data?.error || error.message || 'Erro no faucet'
        appStore.showNotification({ color: 'error', message: msg, timeout: 5000 })
      } finally {
        this.isLoading = false
      }
    },

    async buyTokens(amount, paymentCurrency, paymentAmount) {
      if (!this.address) return
      const appStore = useAppStore()
      this.isLoading = true
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const result = await ExplorerAPI.tokenSale.comprar(
          this.address, amount, paymentCurrency, paymentAmount
        )
        if (result.error) {
          appStore.showNotification({ color: 'error', message: result.error, timeout: 5000 })
          return
        }
        await this.fetchBalance()
        appStore.showNotification({
          color: 'success',
          message: `Compra realizada: ${formatarNano(result.amount || amount)}`,
          timeout: 5000,
        })
      } catch (error) {
        const msg = error?.response?.data?.error || error.message || 'Erro na compra'
        appStore.showNotification({ color: 'error', message: msg, timeout: 5000 })
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Validates a destination address.
     * @param {string} to - Destination address
     * @returns {string|null} Error message or null if valid
     */
    validateAddress(to) {
      if (!to) return 'Endereço destino é obrigatório'
      if (!/^[0-9a-fA-F]{64}$/.test(to)) return 'Endereço inválido (deve ter 64 caracteres hex)'
      if (to === this.address) return 'Não é possível enviar para si mesmo'
      return null
    },

    async sendTransaction(to, value, fee = 21000) {
      if (!this.address) return { success: false, error: 'Carteira não conectada' }

      const addrError = this.validateAddress(to)
      if (addrError) return { success: false, error: addrError }

      if (value <= 0) return { success: false, error: 'Valor deve ser maior que zero' }
      if (value + fee > this.balance) return { success: false, error: 'Saldo insuficiente' }

      const appStore = useAppStore()
      this.isLoading = true
      try {
        const { signTransaction } = await import('@/services/walletCrypto')
        const signingKey = this.signatureScheme === 'dilithium3' ? this.privateKeyData : this.signingKey
        const signature = await signTransaction(signingKey, {
          from: this.address,
          to,
          value,
          fee,
          chainId: appStore.networkStatus.chainId || 3,
        }, this.signatureScheme)

        const { ExplorerAPI } = await import('@/services/api')
        const result = await ExplorerAPI.transacoes.enviar({
          from: this.address,
          to,
          value,
          fee,
          signature,
        })

        // Update balance immediately
        this.balance = Math.max(0, this.balance - value - fee)
        // Then verify with API
        setTimeout(() => this.fetchBalance(), 2000)

        appStore.showNotification({
          color: 'success',
          message: `TX enviada: ${(result.hash || '').slice(0, 16)}...`,
          timeout: 5000,
        })
        return { success: true, hash: result.hash }
      } catch (error) {
        const msg = error?.response?.data?.error || error.message || 'Erro ao enviar'
        appStore.showNotification({ color: 'error', message: msg, timeout: 5000 })
        return { success: false, error: msg }
      } finally {
        this.isLoading = false
      }
    },

    initWebSocketListener() {
      wsClient.onMessage((message) => {
        if (message && message.type === 'new_block' && this.isConnected) {
          this.fetchBalance()
        }
      })
    },
  },
})
